import React, { useState } from 'react';
import { FileText, Image as ImageIcon, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { ProviderBadge } from './ProviderBadge';

const providerColors = {
  openai: 'text-emerald-400',
  anthropic: 'text-orange-400',
  xai: 'text-blue-400',
  deepseek: 'text-purple-400',
  gemini: 'text-cyan-400',
  perplexity: 'text-teal-400',
};

const providerNames = {
  openai: 'OpenAI GPT',
  anthropic: 'Claude',
  xai: 'Grok',
  deepseek: 'DeepSeek',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
};

const ScoresPanel = ({ providerScores, winnerProvider, confidence }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!providerScores || providerScores.length === 0) return null;
  
  const respondedCount = providerScores.filter(p => p.responded).length;
  const totalCount = providerScores.length;
  
  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      {/* Summary header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-gray-300 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            {respondedCount === totalCount ? (
              <Check className="w-3 h-3 text-green-400" />
            ) : (
              <span className="text-yellow-400">{respondedCount}/{totalCount}</span>
            )}
            <span>{respondedCount} models responded</span>
          </span>
          <span className="text-gray-500">•</span>
          <span>Judge confidence: {Math.round(confidence * 100)}%</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {/* Expanded scores */}
      {isExpanded && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-gray-500 font-medium mb-2">Model Scores (0-50):</div>
          {providerScores.map((score, idx) => {
            const isWinner = score.provider === winnerProvider;
            const colorClass = providerColors[score.provider] || 'text-gray-400';
            const displayName = providerNames[score.provider] || score.provider;
            
            return (
              <div
                key={idx}
                className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${
                  isWinner ? 'bg-white/10 border border-white/20' : 'bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isWinner && <Check className="w-3 h-3 text-green-400" />}
                  {!score.responded && <X className="w-3 h-3 text-red-400" />}
                  <span className={`text-xs font-medium ${colorClass}`}>
                    {displayName}
                  </span>
                  {isWinner && (
                    <span className="text-xs text-green-400 font-medium">WINNER</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {score.responded ? (
                    <>
                      <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isWinner ? 'bg-green-400' : 'bg-gray-500'
                          }`}
                          style={{ width: `${(score.total / 50) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-mono ${isWinner ? 'text-green-400' : 'text-gray-400'}`}>
                        {score.total}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-red-400">
                      {score.model === 'skipped' ? 'Skipped' : 'Failed'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const hasAttachments = message.attachments && message.attachments.length > 0;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
            : 'bg-white/5 border border-white/10 text-gray-100'
        }`}
      >
        {/* Attachments for user messages */}
        {isUser && hasAttachments && (
          <div className="flex flex-wrap gap-2 mb-2">
            {message.attachments.map((attachment, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-2 py-1 bg-white/10 rounded-lg"
              >
                {attachment.isImage ? (
                  <>
                    {attachment.preview ? (
                      <img
                        src={attachment.preview}
                        alt={attachment.filename}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-white/80" />
                    )}
                    <span className="text-xs text-white/80 max-w-[80px] truncate">
                      {attachment.filename}
                    </span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 text-white/80" />
                    <span className="text-xs text-white/80 max-w-[80px] truncate">
                      {attachment.filename}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Message content */}
        <div className="whitespace-pre-wrap text-sm leading-relaxed">
          {message.content}
        </div>

        {/* Provider badge for assistant messages */}
        {isAssistant && message.provider && (
          <ProviderBadge
            provider={message.provider}
            model={message.model}
            confidence={message.confidence}
          />
        )}
        
        {/* Scores panel for assistant messages */}
        {isAssistant && message.providerScores && (
          <ScoresPanel
            providerScores={message.providerScores}
            winnerProvider={message.provider}
            confidence={message.confidence}
          />
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
